import React, { useEffect, useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, Tag, Calendar, GraduationCap, BarChart3 } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { useLanguage } from "../i18n/LanguageContext";
import {
  BLOG_ARTICLES,
  findArticleBySlug,
  getArticleI18n,
  BLOG_AUTHOR,
} from "../data/blogArticles";
import { getBlogI18n } from "../i18n/blog";
import { SITE_URL, OG_IMAGE } from "../i18n/seo_routes";

const HREF_MAP = { es: "es-ES", en: "en-US", sk: "sk-SK", de: "de-DE", "zh-CN": "zh-CN" };

const setMeta = (selector, attr, value) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const m = selector.match(/\[([^=]+)="([^"]+)"\]/);
    if (m) el.setAttribute(m[1], m[2]);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const setLink = (rel, href, attrs = {}) => {
  let sel = `link[rel="${rel}"]`;
  Object.entries(attrs).forEach(([k, v]) => (sel += `[${k}="${v}"]`));
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const setJsonLd = (id, data) => {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

const BlogArticlePage = () => {
  const { slug } = useParams();
  const { lang } = useLanguage();
  const ui = getBlogI18n(lang);
  const article = findArticleBySlug(slug);

  // Compute body text length for word count (for schema)
  const wordCount = useMemo(() => {
    if (!article) return 0;
    const i18n = getArticleI18n(article, lang);
    return i18n.sections.reduce(
      (acc, s) =>
        acc +
        s.heading.split(/\s+/).length +
        s.paragraphs.reduce((p, par) => p + par.split(/\s+/).length, 0),
      0
    );
  }, [article, lang]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Inject Article-specific meta + JSON-LD AFTER the global <SEO /> runs.
  // The global SEO uses pageKey=home as fallback for unknown paths; we override here.
  useEffect(() => {
    if (!article) return;
    const i18n = getArticleI18n(article, lang);
    const url = `${SITE_URL}/blog/${article.slug}`;
    const hreflang = HREF_MAP[lang] || "es-ES";

    document.title = `${i18n.title} · Blog RE₿EL`;
    document.documentElement.lang = lang;

    setMeta('meta[name="description"]', "content", i18n.excerpt);
    setMeta('meta[name="keywords"]', "content", article.keywords);
    setMeta('meta[name="author"]', "content", BLOG_AUTHOR.name);
    setMeta(
      'meta[name="robots"]',
      "content",
      "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
    );

    setLink("canonical", url);
    setLink("alternate", `${url}?lang=es`, { hreflang: "es-ES" });
    setLink("alternate", `${url}?lang=en`, { hreflang: "en-US" });
    setLink("alternate", `${url}?lang=sk`, { hreflang: "sk-SK" });
    setLink("alternate", url, { hreflang: "x-default" });

    // OG / Twitter
    setMeta('meta[property="og:type"]', "content", "article");
    setMeta('meta[property="og:title"]', "content", i18n.title);
    setMeta('meta[property="og:description"]', "content", i18n.excerpt);
    setMeta('meta[property="og:url"]', "content", url);
    setMeta('meta[property="og:image"]', "content", article.cover || OG_IMAGE);
    setMeta('meta[property="og:locale"]', "content", hreflang.replace("-", "_"));
    setMeta('meta[property="article:published_time"]', "content", article.date);
    setMeta('meta[property="article:author"]', "content", BLOG_AUTHOR.name);
    setMeta('meta[property="article:section"]', "content", article.category);

    setMeta('meta[name="twitter:card"]', "content", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "content", i18n.title);
    setMeta('meta[name="twitter:description"]', "content", i18n.excerpt);
    setMeta('meta[name="twitter:image"]', "content", article.cover || OG_IMAGE);

    // Article + BreadcrumbList JSON-LD
    const graph = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          "@id": `${url}#article`,
          headline: i18n.title,
          description: i18n.excerpt,
          image: [article.cover || OG_IMAGE],
          datePublished: article.date,
          dateModified: article.date,
          inLanguage: hreflang,
          articleSection: article.category,
          keywords: article.keywords,
          wordCount,
          mainEntityOfPage: { "@type": "WebPage", "@id": url },
          author: {
            "@type": "Organization",
            name: BLOG_AUTHOR.name,
            url: BLOG_AUTHOR.url,
          },
          publisher: {
            "@type": "Organization",
            name: "RE₿EL BTC",
            url: SITE_URL,
            logo: {
              "@type": "ImageObject",
              url: OG_IMAGE,
            },
          },
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "RE₿EL BTC", item: SITE_URL },
            { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
            { "@type": "ListItem", position: 3, name: i18n.title, item: url },
          ],
        },
      ],
    };
    setJsonLd("rebel-jsonld", graph);
  }, [article, lang, wordCount]);

  if (!article) return <Navigate to="/blog" replace />;

  const i18n = getArticleI18n(article, lang);
  const related = BLOG_ARTICLES.filter((a) => a.slug !== article.slug).slice(0, 3);
  const dateFmt = new Date(article.date).toLocaleDateString(lang === "es" ? "es-ES" : lang === "sk" ? "sk-SK" : "en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="min-h-screen bg-black text-white antialiased" data-testid={`blog-article-${article.slug}`}>
      <Navbar />

      {/* HERO */}
      <section className="relative w-full overflow-hidden pb-10 pt-32 md:pt-40">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(251,191,36,0.14),transparent_55%)]" />
        <div className="absolute inset-x-0 top-0 mx-auto h-px max-w-5xl bg-gradient-to-r from-transparent via-white/20 to-transparent" />

        <div className="relative mx-auto max-w-4xl px-6 md:px-12">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-white/50 transition-colors hover:text-white"
            data-testid="blog-back-link"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {ui.back_to_blog}
          </Link>

          <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-amber-400/25 bg-white/[0.03] px-3 py-1.5 backdrop-blur">
            <Tag className="h-3 w-3 text-amber-300" />
            <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-amber-300 md:text-xs">
              {ui.categories[article.category] || article.category}
            </span>
          </div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mt-5 font-display text-3xl font-black leading-[1.1] tracking-tight text-white sm:text-4xl lg:text-5xl"
            data-testid="blog-article-title"
          >
            {i18n.title}
          </motion.h1>

          <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/60 md:text-lg">
            {i18n.excerpt}
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-[11px] uppercase tracking-[0.22em] text-white/40">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" />
              {dateFmt}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              {article.readTime} {ui.min_read}
            </span>
            <span className="inline-flex items-center gap-1.5">
              {ui.written_by}: {BLOG_AUTHOR.name}
            </span>
          </div>
        </div>
      </section>

      {/* COVER IMAGE */}
      <section className="relative mx-auto max-w-5xl px-6 md:px-12">
        <div className="overflow-hidden rounded-3xl border border-white/10 ring-1 ring-amber-400/20">
          <img
            src={article.cover}
            alt={i18n.title}
            className="h-64 w-full object-cover md:h-[420px]"
            loading="eager"
          />
        </div>
      </section>

      {/* BODY */}
      <section className="relative w-full bg-black pb-20 pt-12">
        <article className="mx-auto max-w-3xl px-6 md:px-12" data-testid="blog-article-body">
          {i18n.sections.map((s, idx) => (
            <section key={idx} className={idx === 0 ? "" : "mt-12"}>
              <h2 className="font-display text-2xl font-bold tracking-tight text-white md:text-3xl">
                {s.heading}
              </h2>
              <div className="mt-4 space-y-4 text-[15px] leading-relaxed text-white/75 md:text-base">
                {s.paragraphs.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </section>
          ))}

          <p className="mt-14 rounded-2xl border border-amber-400/20 bg-white/[0.03] p-5 text-[11px] uppercase tracking-[0.22em] text-amber-200/70">
            {ui.disclaimer}
          </p>
        </article>
      </section>

      {/* CTA */}
      <section className="relative w-full pb-24">
        <div className="mx-auto max-w-4xl px-6 md:px-12">
          <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-amber-500/10 via-white/[0.02] to-cyan-500/10 p-8 md:p-12">
            <h3 className="font-display text-2xl font-bold tracking-tight text-white md:text-3xl">
              {ui.cta_title}
            </h3>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-white/60 md:text-base">
              {ui.cta_text}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/academy"
                className="group inline-flex items-center gap-2 rounded-full bg-amber-400 px-5 py-3 text-sm font-bold text-black transition-all hover:-translate-y-0.5 hover:shadow-[0_0_40px_rgba(251,191,36,0.45)]"
                data-testid="blog-cta-academy"
              >
                <GraduationCap className="h-4 w-4" />
                {ui.cta_btn_academy}
              </Link>
              <Link
                to="/simulator"
                className="group inline-flex items-center gap-2 rounded-full border border-cyan-400/40 bg-white/[0.03] px-5 py-3 text-sm font-bold text-cyan-200 transition-all hover:-translate-y-0.5 hover:border-cyan-400 hover:bg-cyan-400/10"
                data-testid="blog-cta-simulator"
              >
                <BarChart3 className="h-4 w-4" />
                {ui.cta_btn_simulator}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* RELATED */}
      <section className="relative w-full pb-32">
        <div className="mx-auto max-w-6xl px-6 md:px-12">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/40">
            {ui.related_label}
          </p>
          <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-3">
            {related.map((rel) => {
              const r = getArticleI18n(rel, lang);
              return (
                <Link
                  key={rel.slug}
                  to={`/blog/${rel.slug}`}
                  className="group flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/[0.025] transition-all hover:-translate-y-1 hover:border-amber-400/30"
                  data-testid={`blog-related-${rel.slug}`}
                >
                  <div className="h-32 w-full overflow-hidden">
                    <img
                      src={rel.cover}
                      alt={r.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-5">
                    <h4 className="font-display text-sm font-bold leading-tight text-white">
                      {r.title}
                    </h4>
                    <span className="mt-3 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.22em] text-amber-300">
                      {ui.read_more}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogArticlePage;
